"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import { ShoppingCart, Trash2Icon } from "lucide-react";

import type { getCart, Schemas } from "@fluid-app/fluid";
import {
  addCartItems,
  removeCartItem,
  removeEnrollmentPack,
  subscribeCartItem,
  unsubscribeCartItem,
} from "@fluid-app/fluid";
import {
  Button,
  Checkbox,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetTrigger,
} from "@fluid-app/ui";

import type { FluidSDK } from "../utils";
import { QuantityModifier } from "../components/QuantityModifier";
import {
  CART_SHEET_EVENTS,
  dispatchCartEvent,
  generateWidgetId,
  getAndCacheSDK,
  initializeWithLocalCart,
  waitForSdkReady,
} from "../utils";
import ShadowDOMWrapper from "../utils/ShadowDOMWrapper";
import { useFluidSettings } from "../utils/widgetSettings";

import "../styles/globals.css";

import type { CartWidgetProps } from "@fluid-app/react-widgets-types";

import { defaultMockCart } from "./mockData";

type CartData = Awaited<ReturnType<typeof getCart>>;

// Helper function to calculate total quantity from cart items
const getTotalQuantityFromCart = (cart: CartData): number => {
  if (!cart?.items) return 0;
  return cart.items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0,
  );
};

// Helper function to calculate quantity from only regular (non-enrollment) items
const getRegularItemQuantityFromCart = (cart: CartData): number => {
  if (!cart?.items) return 0;
  return cart.items
    .filter((item) => !isEnrollmentPackItem(item))
    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
};

// Helper function to calculate quantity from only enrollment pack items
const getEnrollmentItemQuantityFromCart = (cart: CartData): number => {
  if (!cart?.items) return 0;
  return cart.items
    .filter((item) => isEnrollmentPackItem(item))
    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
};

// Helper function to identify enrollment pack items
const isEnrollmentPackItem = (item: Schemas["CartItem"]): boolean => {
  return Boolean(item.enrollment);
};

// Helper function to group items by enrollment pack
const groupCartItems = (
  items: Schemas["CartItem"][] | undefined,
  cart: CartData,
) => {
  if (!items || items.length === 0)
    return { regularItems: [], enrollmentPacks: [] };

  // Create a new array to avoid mutating the original items
  const allItems = [...items];

  // Simple separation based on enrollment flag
  const enrollmentItems = allItems.filter(isEnrollmentPackItem);
  const regularItems = allItems.filter((item) => !isEnrollmentPackItem(item));

  // Group enrollment items into a pack
  const enrollmentPacks: {
    id: string | number;
    title: string;
    price: string;
    items: Schemas["CartItem"][];
    firstItem: Schemas["CartItem"];
  }[] = [];

  // If we have no enrollment items, return early
  if (enrollmentItems.length === 0) {
    return { regularItems, enrollmentPacks };
  }

  // Get enrollment pack details directly from the cart if available
  if (cart?.enrollment_pack && enrollmentItems.length > 0) {
    // Ensure we have at least one item to use as firstItem
    const firstItem = enrollmentItems[0];
    if (!firstItem) {
      return { regularItems, enrollmentPacks };
    }

    // If we have actual enrollment pack data from the API
    const pack = {
      id: cart.enrollment_pack.enrollment_pack_id ?? "enrollment-pack",
      title: cart.enrollment_pack.title ?? "Enrollment Pack",
      price: cart.enrollment_pack.price_in_currency ?? "0",
      items: enrollmentItems,
      firstItem,
    };

    enrollmentPacks.push(pack);
  } else {
    // Fallback for when enrollment_pack is not available
    const defaultItem = enrollmentItems[0];
    if (defaultItem) {
      // Create a single enrollment pack
      const pack = {
        id: "enrollment-pack",
        title: defaultItem.product_title
          ? `${defaultItem.product_title} Pack`
          : "Enrollment Pack",
        price: "0", // Use default since we don't have API data
        items: enrollmentItems,
        firstItem: defaultItem,
      };

      enrollmentPacks.push(pack);
    }
  }

  return { regularItems, enrollmentPacks };
};

/**
 * Preload cart widget styles
 * This can be called early in the application to ensure styles
 * are ready when components are mounted
 */
export function preloadCartWidgetStyles(): void {
  // Skip in server-side rendering
  if (typeof window === "undefined") return;

  // Use window to check global Fluid settings
  interface FluidWindow extends Window {
    __FLUID_STYLE_URL?: string;
  }
  const fluidWindow = window as unknown as FluidWindow;

  // Make sure we have a style URL set
  fluidWindow.__FLUID_STYLE_URL ??= "/dist/styles.css";

  // Import shadow DOM style utilities and preload styles
  try {
    import("../utils/shadowDomStyles")
      .then((module) => {
        // Create a placeholder shadow root to initialize styles
        const tempDiv = document.createElement("div");
        const tempShadow = tempDiv.attachShadow({ mode: "open" });

        // Apply styles to initialize caching
        module.applyStylesIntoShadowRoot(tempShadow);
      })
      .catch((err) => {
        console.warn("Failed to preload cart widget styles:", err);
      });
  } catch (error) {
    console.warn("Could not import shadow DOM style utilities:", error);
  }
}

// In your CartWidget component, add this to ensure it preloads styles on module load
// This is outside any function or class, at module level
// Removed automatic preload to prevent double rendering
// if (typeof window !== "undefined") {
//   // Use setTimeout to ensure this runs after hydration
//   setTimeout(preloadCartWidgetStyles, 0);
// }

export function CartWidget({
  position = "bottom-right",
  size = 60,
  xMargin = 40,
  yMargin = 30,
  onCheckout,
  fluidShopDisplayName,
  hideWidget = false,
  demoMode = false,
  demoCart = null,
}: CartWidgetProps) {
  // Check for server-side rendering early
  if (typeof window === "undefined") {
    return null; // Return null during server-side rendering
  }

  // Use the Fluid settings hook
  const { getDisplayNames, isLoading } = useFluidSettings();

  // Get the display names with proper priority
  const { shopDisplayName, affiliateDisplayName } =
    getDisplayNames(fluidShopDisplayName);

  // Use the effective shop display name based on priority
  const effectiveShopDisplayName = shopDisplayName;

  // Get initial cart data if available or use demoCart in demo mode
  const initialLocalCart = useMemo(() => {
    if (demoMode) {
      // Use provided demoCart or default mock cart
      return demoCart ?? defaultMockCart;
    }
    return initializeWithLocalCart();
  }, [demoMode, demoCart]);

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [cart, setCart] = useState<CartData>(
    initialLocalCart ?? undefined, // Initialize with local cart data if available
  );

  const [itemCount, setItemCount] = useState(
    getTotalQuantityFromCart(initialLocalCart ?? undefined),
  );

  // Add loading state for cart items
  const [isCartLoading, setIsCartLoading] = useState(false);

  // Add loading state for checkout price
  const [isCheckoutPriceLoading, setIsCheckoutPriceLoading] = useState(false);

  // Add a state to prevent brief "empty cart" flash during state transitions
  const [showEmptyState, setShowEmptyState] = useState(false);

  // Add loading states for individual item operations
  const [itemLoadingStates, setItemLoadingStates] = useState<
    Record<number, boolean>
  >({});

  // Check if any item is currently loading
  const isAnyItemLoading = useMemo(
    () => Object.values(itemLoadingStates).some(Boolean),
    [itemLoadingStates],
  );

  // Track animation state with refs to prevent double animations
  const shouldShowAnimationRef = useRef(true);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Create a ref to store the shadow root
  const shadowRootRef = React.useRef<ShadowRoot | null>(null);

  // SDK reference to ensure we use the same instance consistently
  const sdkRef = useRef<FluidSDK | null>(demoMode ? null : getAndCacheSDK());

  // Generate a stable widget ID once for this component instance
  const widgetIdRef = useRef<string>(generateWidgetId("cart-widget"));

  // Calculate if cart has items
  const hasItems = useMemo(() => !!cart?.items?.length, [cart?.items?.length]);

  // Manage empty state display with a small delay to prevent flashing
  useEffect(() => {
    if (isCartLoading) {
      // When loading starts, don't show empty state
      setShowEmptyState(false);
    } else if (!hasItems) {
      // When not loading and no items, show empty state after a small delay
      const timer = setTimeout(() => {
        setShowEmptyState(true);
      }, 150); // Small delay to allow state updates to complete

      return () => clearTimeout(timer);
    } else {
      // When we have items, immediately hide empty state
      setShowEmptyState(false);
    }
  }, [isCartLoading, hasItems]);

  // Mark animation as played after initial render to prevent double animations
  useEffect(() => {
    // Only start animation timing when not loading
    if (isLoading) return;

    // Clear any existing timer
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    // Only set up the timer if animation should be shown
    if (shouldShowAnimationRef.current) {
      animationTimeoutRef.current = setTimeout(() => {
        shouldShowAnimationRef.current = false;
        animationTimeoutRef.current = null;
      }, 600); // Wait for animation to complete (300ms delay + 300ms animation)
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [isLoading]); // Add isLoading as dependency

  // Function to get the current cart with immediate local fallback
  const getCartWithFallback = async (): Promise<CartData> => {
    if (demoMode) return cart;
    if (!sdkRef.current) return undefined;

    try {
      // First try local cart for immediate feedback
      const localCart = sdkRef.current.getLocalCart();
      if (localCart) {
        return localCart;
      }

      // Then try API cart
      return await sdkRef.current.getCart();
    } catch (e) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to get cart:`,
        e,
      );
      return undefined;
    }
  };

  // Get the local cart from SDK and update our state if it's more recent
  const syncWithSdkLocalCart = () => {
    // Skip in demo mode
    if (demoMode) return;

    if (!sdkRef.current) return;

    try {
      const localCart = sdkRef.current.getLocalCart();
      if (!localCart) return;

      // Compare with current state to avoid unnecessary re-renders
      // Check if the items in localCart are different from what we have in state
      if (cart && localCart.items && cart.items) {
        // Compare regular and enrollment items separately to prevent
        // brief quantity updates when adding items that exist in enrollment packs
        const currentRegularItems = getRegularItemQuantityFromCart(cart);
        const localRegularItems = getRegularItemQuantityFromCart(localCart);

        const currentEnrollmentItems = getEnrollmentItemQuantityFromCart(cart);
        const localEnrollmentItems =
          getEnrollmentItemQuantityFromCart(localCart);

        // Get numeric values from amount_total for comparison
        const getCurrentCartTotal = () => {
          if (!cart.amount_total) return 0;
          return parseFloat(cart.amount_total) || 0;
        };

        const getLocalCartTotal = () => {
          if (!localCart.amount_total) return 0;
          return parseFloat(localCart.amount_total) || 0;
        };

        const currentCartTotal = getCurrentCartTotal();
        const localCartTotal = getLocalCartTotal();

        // Update if either the regular item count, enrollment item count, or total amount changed
        // Separate comparison prevents enrollment pack quantities from affecting regular item updates
        if (
          currentRegularItems !== localRegularItems ||
          currentEnrollmentItems !== localEnrollmentItems ||
          Math.abs(currentCartTotal - localCartTotal) > 0.01
        ) {
          setCart(localCart);
          setItemCount(getTotalQuantityFromCart(localCart));
        }
      } else if (!cart) {
        // If we don't have a cart but local storage does, use it
        setCart(localCart);
        setItemCount(getTotalQuantityFromCart(localCart));
      }
    } catch (e) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Error syncing with local cart:`,
        e,
      );
    }
  };

  // Initial cart load
  useEffect(
    () => {
      // Skip SDK initialization in demo mode
      if (demoMode) return;

      // Generate a stable log prefix
      const logPrefix = `Cart widget [${widgetIdRef.current}]`;

      // Use a ref to track if this effect is still valid (component hasn't unmounted)
      const abortController = new AbortController();
      let cleanup: {
        removeEventListeners: () => void;
        clearIntervalFunction: () => void;
      } | null = null;

      // Define the main initialization function
      const initializeCartWidget = async () => {
        try {
          // Check if we should abort
          if (abortController.signal.aborted) return null;

          // Wait for the SDK to be ready before proceeding
          await waitForSdkReady();

          // Now it's safe to use SDK functions
          sdkRef.current = getAndCacheSDK();

          // Sync with local cart first for immediate feedback
          syncWithSdkLocalCart();

          // Then fetch from API for complete data
          const freshCart = await getCartWithFallback();

          if (freshCart) {
            setCart(freshCart);
            setItemCount(getTotalQuantityFromCart(freshCart));
            localStorage.setItem("cart_last_updated", Date.now().toString());
          }

          // Set up event listeners for cart updates from other components
          const handleCartUpdate = (event: Event) => {
            // Don't process events if component is unmounting
            if (abortController.signal.aborted) return;

            // Properly type the custom event
            const cartEvent = event as CustomEvent<
              | {
                  handledByWidget: boolean;
                  widgetId: string;
                }
              | null
              | undefined
            >;

            // Check if this is our own event to prevent loops
            const eventDetail = cartEvent.detail;
            if (
              eventDetail &&
              eventDetail.handledByWidget &&
              eventDetail.widgetId === widgetIdRef.current
            ) {
              return;
            }

            // If another component updated the cart, we need to sync
            // Add a small delay to avoid updating while other components are still updating
            setTimeout(() => {
              if (!abortController.signal.aborted) {
                syncWithSdkLocalCart();
              }
            }, 300);
          };

          // Only add event listeners if component is still mounted
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (!abortController.signal.aborted) {
            window.addEventListener("ADD_TO_CART", handleCartUpdate);
            window.addEventListener("UPDATE_CART_ITEM", handleCartUpdate);
            window.addEventListener("REMOVE_FROM_CART", handleCartUpdate);
            window.addEventListener("LOCALE_CHANGE", handleCartUpdate);

            // Set up a polling interval for cart updates
            const syncInterval = setInterval(() => {
              if (!abortController.signal.aborted) {
                syncWithSdkLocalCart();
              }
            }, 2000); // Check every 2 seconds

            // Store event cleanup functions
            const removeEventListeners = () => {
              window.removeEventListener("ADD_TO_CART", handleCartUpdate);
              window.removeEventListener("UPDATE_CART_ITEM", handleCartUpdate);
              window.removeEventListener("REMOVE_FROM_CART", handleCartUpdate);
              window.removeEventListener("LOCALE_CHANGE", handleCartUpdate);
            };

            // Store the interval cleanup
            const clearIntervalFunction = () => clearInterval(syncInterval);

            // Return both cleanup functions
            return {
              removeEventListeners,
              clearIntervalFunction,
            };
          }

          return null;
        } catch (error) {
          console.error(
            `${logPrefix}: Failed to initialize cart widget:`,
            error,
          );
          return null;
        }
      };

      // Start the initialization process
      initializeCartWidget()
        .then((result) => {
          if (!abortController.signal.aborted) {
            cleanup = result;
          }
        })
        .catch((error) => {
          console.error(`Failed to initialize cart widget:`, error);
        });

      // Return a cleanup function that properly handles the async case
      return () => {
        // Signal that this effect is being cleaned up
        abortController.abort();

        // Clean up any listeners that were already set up
        if (cleanup) {
          cleanup.removeEventListeners();
          cleanup.clearIntervalFunction();
        }
      };
    },
    [demoMode], // Add demoMode as a dependency
  );

  // Listen for cart sheet control events
  useEffect(() => {
    const handleOpenCart = () => {
      setIsOpen(true);
    };

    const handleCloseCart = () => {
      setIsOpen(false);
    };

    const handleToggleCart = () => {
      setIsOpen((prev) => !prev);
    };

    // Add event listeners for cart sheet events
    window.addEventListener(CART_SHEET_EVENTS.OPEN_CART, handleOpenCart);
    window.addEventListener(CART_SHEET_EVENTS.CLOSE_CART, handleCloseCart);
    window.addEventListener(CART_SHEET_EVENTS.TOGGLE_CART, handleToggleCart);

    // Clean up by removing event listeners
    return () => {
      window.removeEventListener(CART_SHEET_EVENTS.OPEN_CART, handleOpenCart);
      window.removeEventListener(CART_SHEET_EVENTS.CLOSE_CART, handleCloseCart);
      window.removeEventListener(
        CART_SHEET_EVENTS.TOGGLE_CART,
        handleToggleCart,
      );
    };
  }, []);

  // Listen for cart loading events
  useEffect(() => {
    const handleCartLoadingStart = () => {
      setIsCartLoading(true);
    };

    const handleCartLoadingEnd = () => {
      // Sync with SDK local cart immediately when loading ends
      // to ensure we have the latest cart state before setting loading to false
      syncWithSdkLocalCart();

      // Add a small delay to ensure the cart has time to update
      setTimeout(() => {
        setIsCartLoading(false);
      }, 100); // Reduced delay from 500ms to 100ms for better UX
    };

    // Add event listeners for cart loading events
    window.addEventListener(
      CART_SHEET_EVENTS.CART_LOADING_START,
      handleCartLoadingStart,
    );
    window.addEventListener(
      CART_SHEET_EVENTS.CART_LOADING_END,
      handleCartLoadingEnd,
    );

    // Clean up event listeners
    return () => {
      window.removeEventListener(
        CART_SHEET_EVENTS.CART_LOADING_START,
        handleCartLoadingStart,
      );
      window.removeEventListener(
        CART_SHEET_EVENTS.CART_LOADING_END,
        handleCartLoadingEnd,
      );
    };
  }, []);

  // Handler for incrementing item quantity
  const handleIncrement = async (variantId: number): Promise<void> => {
    if (!cart?.items) return;

    // Only find non-enrollment items with the matching variant_id
    const item = cart.items.find(
      (i: Schemas["CartItem"]) =>
        i.variant?.id === variantId && !isEnrollmentPackItem(i),
    );
    if (!item) return;

    // Set loading state for this specific item
    setItemLoadingStates((prev) => ({ ...prev, [variantId]: true }));

    // If demo mode, update state directly
    if (demoMode) {
      const currentQuantity = Number(item.quantity) || 0;
      const updatedItems = cart.items.map((i: Schemas["CartItem"]) =>
        i.variant?.id === variantId && !isEnrollmentPackItem(i)
          ? { ...i, quantity: currentQuantity + 1 }
          : i,
      );
      const updatedCart = {
        ...cart,
        items: updatedItems,
        recurring: cart.recurring,
      };
      setCart(updatedCart);
      setItemCount(getTotalQuantityFromCart(updatedCart));

      // Dispatch local event for tracking in demo mode
      dispatchCartEvent(
        "UPDATE_CART_ITEM",
        {
          itemId: variantId,
          quantity: currentQuantity + 1,
          action: "increment",
          itemDetails: item,
          timestamp: new Date().toISOString(),
          demoMode: true,
        },
        widgetIdRef.current,
      );

      // Remove loading state
      setTimeout(() => {
        setItemLoadingStates((prev) => ({ ...prev, [variantId]: false }));
      }, 300);
      return;
    }

    try {
      // Call SDK function and wait for response
      await addCartItems(variantId, { quantity: 1 });

      // Dispatch event for tracking
      dispatchCartEvent(
        "UPDATE_CART_ITEM",
        {
          itemId: variantId,
          quantity: (Number(item.quantity) || 0) + 1,
          action: "increment",
          itemDetails: item,
          timestamp: new Date().toISOString(),
        },
        widgetIdRef.current,
      );

      // Refresh cart from SDK
      const freshCart = await getCartWithFallback();
      if (freshCart) {
        setCart(freshCart);
        setItemCount(getTotalQuantityFromCart(freshCart));
        localStorage.setItem("cart_last_updated", Date.now().toString());
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to increment item ${variantId}:`,
        errorMessage,
      );
    } finally {
      // Remove loading state
      setItemLoadingStates((prev) => ({ ...prev, [variantId]: false }));
    }
  };

  // Handler for decrementing item quantity
  const handleDecrement = async (variantId: number): Promise<void> => {
    if (!cart?.items) return;

    // Only find non-enrollment items with the matching variant_id
    const item = cart.items.find(
      (i: Schemas["CartItem"]) =>
        i.variant?.id === variantId && !isEnrollmentPackItem(i),
    );
    if (!item) return;

    const currentQuantity = Number(item.quantity) || 0;
    if (currentQuantity <= 0) return;

    // Set loading state for this specific item
    setItemLoadingStates((prev) => ({ ...prev, [variantId]: true }));

    // If demo mode, update state directly
    if (demoMode) {
      const isRemoval = currentQuantity === 1;
      const newQuantity = currentQuantity - 1;
      let updatedItems;
      if (isRemoval) {
        // Remove the item entirely
        updatedItems = cart.items.filter(
          (i: Schemas["CartItem"]) =>
            i.variant?.id !== variantId || isEnrollmentPackItem(i),
        );
      } else {
        // Decrement quantity
        updatedItems = cart.items.map((i: Schemas["CartItem"]) =>
          i.variant?.id === variantId && !isEnrollmentPackItem(i)
            ? { ...i, quantity: newQuantity }
            : i,
        );
      }
      const updatedCart = {
        ...cart,
        items: updatedItems,
        recurring: cart.recurring,
      };
      setCart(updatedCart);
      setItemCount(getTotalQuantityFromCart(updatedCart));

      // Dispatch local event for tracking in demo mode
      dispatchCartEvent(
        isRemoval ? "REMOVE_FROM_CART" : "UPDATE_CART_ITEM",
        {
          itemId: isRemoval ? item.id : variantId,
          quantity: isRemoval ? undefined : newQuantity,
          action: "decrement",
          itemDetails: item,
          timestamp: new Date().toISOString(),
          demoMode: true,
        },
        widgetIdRef.current,
      );

      // Remove loading state
      setTimeout(() => {
        setItemLoadingStates((prev) => ({ ...prev, [variantId]: false }));
      }, 300);
      return;
    }

    try {
      // Call SDK function and wait for response
      await removeCartItem(variantId, { quantity: 1 });

      const isRemoval = currentQuantity === 1;
      // Dispatch event for tracking
      dispatchCartEvent(
        isRemoval ? "REMOVE_FROM_CART" : "UPDATE_CART_ITEM",
        {
          itemId: isRemoval ? item.id : variantId,
          quantity: isRemoval ? undefined : currentQuantity - 1,
          action: "decrement",
          itemDetails: item,
          timestamp: new Date().toISOString(),
        },
        widgetIdRef.current,
      );

      // Refresh cart from SDK
      const freshCart = await getCartWithFallback();
      if (freshCart) {
        setCart(freshCart);
        setItemCount(getTotalQuantityFromCart(freshCart));
        localStorage.setItem("cart_last_updated", Date.now().toString());
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to decrement item ${variantId}:`,
        errorMessage,
      );
    } finally {
      // Remove loading state
      setItemLoadingStates((prev) => ({ ...prev, [variantId]: false }));
    }
  };

  // Handle checkout
  const handleCheckout = async (): Promise<void> => {
    try {
      // Set loading state to true when checkout starts
      setIsCheckoutPriceLoading(true);

      // If custom onCheckout is provided, use it
      if (onCheckout) {
        onCheckout();
        // Reset loading state after executing the custom onCheckout
        setIsCheckoutPriceLoading(false);
        return;
      }

      // In demo mode, just log the checkout action
      if (demoMode) {
        console.log("Demo mode: Checkout initiated with", {
          cartId: cart?.id,
          cartTotal: cart?.sub_total_in_currency,
          itemCount,
        });

        // Dispatch event for tracking in demo mode
        dispatchCartEvent(
          "CHECKOUT_INITIATED",
          {
            cartId: cart?.id,
            cartTotal: cart?.sub_total_in_currency,
            itemCount,
            timestamp: new Date().toISOString(),
            demoMode: true,
          },
          widgetIdRef.current,
        );

        // Show a demo message to the user that would normally be redirected
        alert(
          "Demo Mode: In production, you would be redirected to the checkout page now.",
        );
        // Reset loading state in demo mode
        setIsCheckoutPriceLoading(false);
        return;
      }

      if (!sdkRef.current) {
        throw new Error("SDK not initialized");
      }

      // Track checkout initiated event
      dispatchCartEvent(
        "CHECKOUT_INITIATED",
        {
          cartId: cart?.id,
          cartTotal: cart?.sub_total_in_currency,
          itemCount,
        },
        widgetIdRef.current,
      );

      // Call the SDK's checkout function which will handle redirection
      await sdkRef.current.checkout();

      // Note: The checkout function doesn't return a URL, it redirects automatically
      // If we reach this point, the redirection might have been blocked
    } catch (error) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to checkout:`,
        error,
      );
      // Set loading state to false on error
      setIsCheckoutPriceLoading(false);
    } finally {
      // Reset loading state in case redirection hasn't happened
      // or the checkout process has stalled
      setTimeout(() => {
        setIsCheckoutPriceLoading(false);
      }, 5000); // Add a timeout as a safeguard if redirection is delayed
    }
  };

  // Position classes for the cart widget (for backwards compatibility)
  const positionClasses = {
    "top-left": "top-0 left-0",
    "top-right": "top-0 right-0",
    "bottom-left": "bottom-0 left-0",
    "bottom-right": "bottom-0 right-0",
  };

  return (
    <ShadowDOMWrapper shadowRootRef={shadowRootRef}>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        {!hideWidget && (
          <SheetTrigger
            className={`fixed border-none ${positionClasses[position]} ${
              shouldShowAnimationRef.current ? "widget-slide-in" : ""
            } z-30 flex translate-y-0 transform cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-md transition-transform duration-300 ease-in-out`}
            style={{
              width: `${size}px`,
              height: `${size}px`,
              margin: `${yMargin}px ${xMargin}px`,
            }}
            aria-label="Open cart"
            data-fluid-trigger="true"
            icon={
              <div className="relative flex items-center justify-center">
                {/* Cart icon */}
                <ShoppingCart className="h-6 w-6" />

                {/* Item count badge */}
                {itemCount > 0 && (
                  <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-primary">
                    {itemCount}
                  </span>
                )}
              </div>
            }
          />
        )}

        <SheetContent
          className="fixed bottom-0 right-0 top-0 z-[9999] flex h-full w-full max-w-md flex-col overflow-hidden bg-background px-4 pb-0 pt-4 shadow-lg"
          side="right"
          containerRoot={shadowRootRef.current ?? undefined}
        >
          {/* Header - matching original design */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Your cart from</p>
              <div className="text-[18px] font-bold text-foreground">
                {effectiveShopDisplayName}
              </div>
            </div>
          </div>

          {/* Checkout button - matching original design */}
          <Button
            onClick={handleCheckout}
            disabled={
              !hasItems ||
              isCartLoading ||
              isCheckoutPriceLoading ||
              isAnyItemLoading
            }
            variant="default"
            size={"lg"}
            className="flex h-12 w-full items-center justify-center rounded-sm bg-primary font-semibold text-white sm:h-10"
            isLoading={
              (isCartLoading || isCheckoutPriceLoading) && !isAnyItemLoading
            }
          >
            {!isCheckoutPriceLoading && !isCartLoading
              ? cart?.sub_total_in_currency
                ? `${cart.sub_total_in_currency} - Checkout`
                : "$0.00"
              : null}
          </Button>

          <ScrollArea className="mt-4 flex-1 overflow-y-auto">
            {isCartLoading ? (
              <div className="flex h-full w-full items-center justify-center py-10 text-center text-muted-foreground">
                Loading cart...
              </div>
            ) : showEmptyState ? (
              <div className="px-4 py-10 text-center text-muted-foreground">
                Your cart is empty
              </div>
            ) : (
              <div>
                {/* Group items by enrollment pack */}
                {(() => {
                  const { regularItems, enrollmentPacks } = groupCartItems(
                    cart?.items,
                    cart,
                  );

                  return (
                    <>
                      {/* Render enrollment packs first */}
                      {enrollmentPacks.map((pack, packIndex) => (
                        <div
                          key={`pack-${pack.id}`}
                          className={`mb-4 pb-4 ${
                            packIndex < enrollmentPacks.length - 1 ||
                            regularItems.length > 0
                              ? "border-b border-border"
                              : ""
                          }`}
                        >
                          {/* Enrollment Pack Header */}
                          <div className="mb-2 flex items-start">
                            {/* Pack Image */}
                            <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm">
                              <img
                                src={
                                  pack.firstItem.variant?.image_url ??
                                  pack.firstItem.product?.image_url ??
                                  "https://placehold.co/60x60?text=Pack"
                                }
                                alt={pack.title}
                                className="h-full w-full object-cover"
                              />
                            </div>

                            {/* Pack Details */}
                            <div className="flex flex-1 flex-col px-3">
                              <div className="font-semibold text-foreground">
                                {pack.title}
                              </div>
                              <div className="-mt-0.5 mb-0.5 text-xs text-muted-foreground">
                                (Enrollment Pack)
                              </div>
                              <p className="text-sm font-semibold text-foreground">
                                {pack.price}
                              </p>
                            </div>

                            {/* Remove button for entire pack */}
                            <div className="ml-2 self-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 p-0"
                                isLoading={
                                  itemLoadingStates[
                                    pack.firstItem.variant?.id ?? 0
                                  ] ?? false
                                }
                                disabled={
                                  isAnyItemLoading ||
                                  (itemLoadingStates[
                                    pack.firstItem.variant?.id ?? 0
                                  ] ??
                                    false)
                                }
                                onClick={async () => {
                                  const variantId =
                                    pack.firstItem.variant?.id ?? 0;

                                  // Set loading state
                                  setItemLoadingStates((prev) => ({
                                    ...prev,
                                    [variantId]: true,
                                  }));

                                  // If in demo mode, update state directly
                                  if (demoMode) {
                                    const updatedItems = cart?.items?.filter(
                                      (i) => !i.enrollment,
                                    );

                                    const updatedCart = {
                                      ...cart,
                                      items: updatedItems,
                                      recurring: cart?.recurring ?? [],
                                    };
                                    setCart(updatedCart);
                                    setItemCount(
                                      getTotalQuantityFromCart(updatedCart),
                                    );

                                    dispatchCartEvent(
                                      "REMOVE_FROM_CART",
                                      {
                                        itemId: pack.firstItem.id,
                                        action: "remove-enrollment-pack",
                                        itemDetails: pack.firstItem,
                                        timestamp: new Date().toISOString(),
                                        demoMode: true,
                                      },
                                      widgetIdRef.current,
                                    );

                                    setTimeout(() => {
                                      setItemLoadingStates((prev) => ({
                                        ...prev,
                                        [variantId]: false,
                                      }));
                                    }, 300);
                                    return;
                                  }

                                  try {
                                    // Remove the entire enrollment pack using its ID
                                    if (sdkRef.current) {
                                      // Use the enrollment pack ID from the cart
                                      const enrollmentPackId =
                                        cart?.enrollment_pack
                                          ?.enrollment_pack_id ?? pack.id;

                                      // Convert to number if it's a string
                                      const packId =
                                        typeof enrollmentPackId === "number"
                                          ? enrollmentPackId
                                          : typeof enrollmentPackId === "string"
                                            ? parseInt(enrollmentPackId, 10)
                                            : (pack.firstItem.variant?.id ?? 0);

                                      await removeEnrollmentPack(packId);
                                    }

                                    // Refresh cart from SDK
                                    const freshCart =
                                      await getCartWithFallback();
                                    if (freshCart) {
                                      setCart(freshCart);
                                      setItemCount(
                                        getTotalQuantityFromCart(freshCart),
                                      );
                                      localStorage.setItem(
                                        "cart_last_updated",
                                        Date.now().toString(),
                                      );
                                    }
                                  } catch (error) {
                                    console.error(
                                      `Cart widget [${widgetIdRef.current}]: Failed to remove enrollment pack:`,
                                      error,
                                    );
                                  } finally {
                                    setItemLoadingStates((prev) => ({
                                      ...prev,
                                      [variantId]: false,
                                    }));
                                  }
                                }}
                              >
                                <Trash2Icon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Sub-items */}
                          <div className="ml-6 border-l border-border pl-4">
                            {pack.items.map((item, itemIndex) => (
                              <div
                                key={`subitem-${item.id ?? `variant-${item.variant?.id}`}`}
                                className={`flex items-center py-2 ${
                                  itemIndex < pack.items.length - 1
                                    ? "border-b border-border"
                                    : ""
                                }`}
                              >
                                {/* Small thumbnail */}
                                <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-sm">
                                  <img
                                    src={
                                      item.variant?.image_url ??
                                      item.product?.image_url ??
                                      "https://placehold.co/60x60?text=Product"
                                    }
                                    alt={item.product_title ?? "Product"}
                                    className="h-full w-full object-cover"
                                  />
                                </div>

                                {/* Item title */}
                                <div className="flex-1 px-3">
                                  <div className="text-sm text-foreground">
                                    {item.product_title}
                                  </div>
                                </div>

                                {/* Quantity (only display if more than 1) */}
                                {Number(item.quantity) > 1 && (
                                  <div className="text-sm text-muted-foreground">
                                    x {Number(item.quantity)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {/* Render regular items after enrollment packs */}
                      {regularItems.map((item, index) => (
                        <div
                          key={item.id ?? `variant-${item.variant?.id}`}
                          className={`mb-4 flex items-start pb-4 ${
                            index < regularItems.length - 1
                              ? "border-b border-border"
                              : ""
                          }`}
                        >
                          {/* Product Image */}
                          <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm">
                            <img
                              src={
                                item.variant?.image_url ??
                                item.product?.image_url ??
                                "https://placehold.co/60x60?text=Product"
                              }
                              alt={item.product_title ?? "Product"}
                              className="h-full w-full object-cover"
                            />
                          </div>

                          {/* Product Details */}
                          <div className="flex flex-1 flex-col px-3">
                            <div className="font-semibold text-foreground">
                              {item.product_title}
                            </div>
                            <p className="mt-0.5 text-sm font-semibold text-foreground">
                              {/* Display subscription price when item is subscribed and has subscription price */}
                              {(() => {
                                const hasSubscription = Boolean(
                                  item.subscription,
                                );
                                const hasSubscriptionPrice = Boolean(
                                  item.subscription_price_in_currency,
                                );
                                const regularPrice = item.price_in_currency;
                                const subscriptionPrice =
                                  item.subscription_price_in_currency;

                                // Show subscription price if item is subscribed AND has a subscription price
                                // Otherwise fall back to regular price
                                const displayPrice =
                                  hasSubscription && hasSubscriptionPrice
                                    ? subscriptionPrice
                                    : regularPrice;

                                return displayPrice;
                              })()}

                              {item.subscription &&
                                item.subscribe_and_save_for_display && (
                                  <span className="ml-2 text-xs text-green-600">
                                    {item.subscribe_and_save_for_display}
                                  </span>
                                )}
                            </p>

                            {/* Subscription toggle */}
                            {item.allow_subscription && (
                              <div className="mt-1 flex items-center">
                                <div className="flex items-center">
                                  <Checkbox
                                    id={`subscribe-${item.id}`}
                                    checked={!!item.subscription}
                                    onCheckedChange={async (checked) => {
                                      if (!item.id) return;

                                      // Set checkout price to loading state
                                      setIsCheckoutPriceLoading(true);

                                      // Store the original subscribe_and_save_for_display value
                                      const savingsDisplay =
                                        item.subscribe_and_save_for_display ??
                                        "";

                                      if (
                                        savingsDisplay &&
                                        !item.subscription
                                      ) {
                                        localStorage.setItem(
                                          `base_savings_${item.id}`,
                                          savingsDisplay,
                                        );
                                      }

                                      // Skip API calls in demo mode
                                      if (demoMode) {
                                        // Simulate API delay then update the cart with new subscription status
                                        setTimeout(() => {
                                          if (cart?.items) {
                                            const baseSavingsDisplay =
                                              localStorage.getItem(
                                                `base_savings_${item.id}`,
                                              ) ?? savingsDisplay;

                                            const updatedItems = cart.items.map(
                                              (i) => {
                                                if (i.id === item.id) {
                                                  // When subscribing, make sure we have a subscription price
                                                  // In demo mode, calculate a subscription price if not available
                                                  let subscriptionPrice =
                                                    i.subscription_price_in_currency;
                                                  if (
                                                    checked &&
                                                    !subscriptionPrice &&
                                                    i.price_in_currency
                                                  ) {
                                                    // Create a demo subscription price (20% off regular price)
                                                    const regularPriceMatch =
                                                      /[\d.]+/.exec(
                                                        i.price_in_currency,
                                                      );
                                                    if (regularPriceMatch) {
                                                      const regularPrice =
                                                        parseFloat(
                                                          regularPriceMatch[0],
                                                        );
                                                      const subscriptionPriceValue =
                                                        (
                                                          regularPrice * 0.8
                                                        ).toFixed(2);
                                                      subscriptionPrice =
                                                        i.price_in_currency.replace(
                                                          /[\d.]+/,
                                                          subscriptionPriceValue,
                                                        );
                                                    }
                                                  }

                                                  return {
                                                    ...i,
                                                    subscription: !!checked,
                                                    subscription_price_in_currency:
                                                      checked
                                                        ? subscriptionPrice
                                                        : i.subscription_price_in_currency,
                                                    subscribe_and_save_for_display:
                                                      checked
                                                        ? baseSavingsDisplay
                                                        : undefined,
                                                  };
                                                }
                                                return i;
                                              },
                                            );

                                            const updatedCart = {
                                              ...cart,
                                              items: updatedItems,
                                              recurring: cart.recurring,
                                            };
                                            setCart(updatedCart);
                                            setIsCheckoutPriceLoading(false);
                                          }
                                        }, 500);
                                        return;
                                      }

                                      // Dispatch event for the update
                                      dispatchCartEvent(
                                        "UPDATE_CART_ITEM",
                                        {
                                          itemId: item.id,
                                          action: item.subscription
                                            ? "unsubscribe"
                                            : "subscribe",
                                          itemDetails: item,
                                          timestamp: new Date().toISOString(),
                                        },
                                        widgetIdRef.current,
                                      );

                                      try {
                                        let updatedCartData;

                                        if (!checked) {
                                          if (typeof item.id === "number") {
                                            updatedCartData =
                                              await unsubscribeCartItem(
                                                item.id,
                                              );
                                          }
                                        } else {
                                          if (typeof item.id === "number") {
                                            updatedCartData =
                                              await subscribeCartItem(item.id);
                                          }
                                        }

                                        if (updatedCartData) {
                                          // Handle response structure
                                          const freshCart =
                                            "cart" in updatedCartData
                                              ? (updatedCartData.cart as Awaited<
                                                  ReturnType<typeof getCart>
                                                >)
                                              : (updatedCartData as Awaited<
                                                  ReturnType<typeof getCart>
                                                >);

                                          setCart(freshCart);
                                          setItemCount(
                                            getTotalQuantityFromCart(freshCart),
                                          );
                                          localStorage.setItem(
                                            "cart_last_updated",
                                            Date.now().toString(),
                                          );
                                        }
                                      } catch (error) {
                                        console.error(
                                          `Cart widget [${widgetIdRef.current}]: Failed to ${
                                            !checked
                                              ? "unsubscribe from"
                                              : "subscribe to"
                                          } item ${item.id}:`,
                                          error,
                                        );
                                      } finally {
                                        setIsCheckoutPriceLoading(false);
                                      }
                                    }}
                                    className="mr-1.5"
                                  />
                                  <label
                                    htmlFor={`subscribe-${item.id}`}
                                    className="cursor-pointer text-xs text-muted-foreground"
                                  >
                                    {item.subscription
                                      ? item.subscription_interval
                                        ? `Every ${item.subscription_interval} ${
                                            item.subscription_interval === 1
                                              ? "month"
                                              : "months"
                                          }`
                                        : "Subscribed"
                                      : (item.subscribe_and_save_for_display ??
                                        `Save ${item.subscription_price_in_currency}`)}
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Quantity controls for regular items */}
                          <div className="ml-2 self-center">
                            <QuantityModifier
                              quantity={Number(item.quantity) || 0}
                              onIncrement={() =>
                                handleIncrement(item.variant?.id ?? 0)
                              }
                              onRemove={() =>
                                handleDecrement(item.variant?.id ?? 0)
                              }
                              disabled={
                                isAnyItemLoading &&
                                !itemLoadingStates[item.variant?.id ?? 0]
                              }
                              isLoading={
                                itemLoadingStates[item.variant?.id ?? 0] ??
                                false
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          <SheetFooter className="-mx-4 -mb-0 border-t px-4 pb-4 pt-4 text-center text-sm text-muted-foreground">
            {affiliateDisplayName
              ? `Powered by ${effectiveShopDisplayName} x ${affiliateDisplayName}`
              : `Powered by ${effectiveShopDisplayName}`}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </ShadowDOMWrapper>
  );
}

export default CartWidget;
